import type { MutableState } from '../State';
import { Feature, sendAnalyticsEvents } from '../analytics';
import {
  ChatbotMessagePayloadInput,
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import { encodePacket } from '../utils/encoder';
import { SyncActionAPI } from '../utils/types';
import {
  getCurrentChallengeWallpaper,
  isInviteChallengeRunning,
} from './social.getters';
import * as Friends from '../friends';
import { isBuruFriend } from '../friends/friends.getters';
import { successInviteRewardGems } from './social.ruleset';

export function recordInvites(
  state: MutableState,
  friendIds: string[],
  timeSent: number,
): void {
  friendIds.forEach((friendId) => {
    const maskedId = encodePacket(friendId);
    state.social.invites[maskedId] = {
      timeSent,
      referralRewardClaimed: false,
    };
  });

  if (isInviteChallengeRunning(state, timeSent)) {
    state.social.challenge.counter += friendIds.length;
  }
}

export function setAllReferralRewardsClaimed(state: MutableState): number {
  let claimCount = 0;
  Object.values(state.social.invites).forEach((invite) => {
    if (!invite.referralRewardClaimed) {
      invite.referralRewardClaimed = true;
      claimCount += 1;
    }
  });

  return claimCount;
}

export function claimWallpaperChallengeReward(
  state: MutableState,
  api: SyncActionAPI,
): void {
  const wallpaperId = getCurrentChallengeWallpaper(state);

  const payload = generateReplicantChatbotPayload({
    feature: Feature.INVITE._,
    $subFeature: Feature.INVITE.WALLPAPER_REWARD,
  });

  // Send analytics events.
  sendAnalyticsEvents(api, [
    {
      eventType: 'InviteWallpaperClaimed',
      eventProperties: {
        wallpaperId,
        startTime: state.social.challenge.startTime,
        timeToComplete: api.date.now() - state.social.challenge.startTime,
        invites: state.social.challenge.counter,
        resetTimes: state.social.challenge.resetTimes,
        claimedWallpapersCount: state.social.challenge.claimedWallpapers.length,
      },
    },
  ]);

  grantWallpaper(api, wallpaperId, payload);

  state.social.challenge.claimed = true;
}

function grantWallpaper(
  api: SyncActionAPI,
  wallpaperId: 'candyland' | 'kingkong',
  tracking: ChatbotMessagePayloadInput,
) {
  const payload = generateReplicantChatbotPayload(tracking);

  const id1 = `wallpaper_${wallpaperId}_1080` as const;
  if (id1 in chatbotMessageTemplates) {
    api.chatbot.sendMessage(
      api.getUserID(),
      chatbotMessageTemplates[id1 as keyof typeof chatbotMessageTemplates]({
        args: {},
        payload,
      }),
      { forceChannel: 'chatbot' },
    );
  }

  const id2 = `wallpaper_${wallpaperId}_1170` as const;
  if (id2 in chatbotMessageTemplates) {
    api.chatbot.sendMessage(
      api.getUserID(),
      chatbotMessageTemplates[id2 as keyof typeof chatbotMessageTemplates]({
        args: {},
        payload,
      }),
      { forceChannel: 'chatbot' },
    );
  }
}

/**
 * Records a referral friend join, synchronizing friend lists and rewards.
 *
 * @param state - Current mutable state that will be updated.
 * @param friendId - Identifier for the friend who joined via referral.
 * @param now - Timestamp capturing when the referral was completed.
 */
export function friendJoinedViaReferral(
  state: MutableState,
  friendId: string,
  now: number,
): void {
  if (isBuruFriend(state, friendId)) {
    return;
  }

  // Mark referred friend.
  state.social.referral.referred[friendId] = {
    createdAt: now,
  };

  // Add invitee as friend on inviter side.
  Friends.modifiers.addFriend(state, friendId, now);

  // Grant reward (was deferred in Phase A).
  state.social.referral.pendingRewards.push({
    friendId,
    reward: { type: 'gems', value: successInviteRewardGems },
    createdAt: now,
  });
}
